import { UserCircle, LogOut, Building, Settings, Shield, ChevronDown, Moon, Sun, Monitor, HelpCircle, LifeBuoy } from "lucide-react";
import { useAuth } from "@/providers/AuthProvider";
import { useTour } from "@/hooks/useTour";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import { useState, useEffect } from "react";
import { useOrganizationSwitcher } from "@/hooks/useOrganizationSwitcher";
import { supabase } from "@/integrations/supabase/client";
import { useTranslation } from "react-i18next";
import { useSettings } from "@/providers/SettingsProvider";
import { Link } from "react-router-dom";

interface UserProfileProps {
	collapsed: boolean;
}

interface UserProfileData {
	id: string;
	first_name?: string;
	last_name?: string;
}

export const UserProfile = ({ collapsed }: UserProfileProps) => {
	const { user, signOut, organizations, currentOrganization, isSuperAdmin, isAdmin } = useAuth();
	const tour = useTour();
	const { switchOrganization, isLoading } = useOrganizationSwitcher();
	const [switchingOrgId, setSwitchingOrgId] = useState<string | null>(null);
	const [profileData, setProfileData] = useState<UserProfileData | null>(null);
	const { t } = useTranslation();
	const { colorMode, setColorModeValue } = useSettings();
	const isDark = colorMode === "dark"
		|| (colorMode === "auto" && window.matchMedia("(prefers-color-scheme: dark)").matches);

	const cycleColorMode = () => {
		const next = colorMode === "light" ? "dark" : colorMode === "dark" ? "auto" : "light";
		setColorModeValue(next);
	};

	const colorModeLabel = colorMode === "auto"
		? t("settings.displaySettings.colorMode.auto")
		: isDark ? t("settings.displaySettings.colorMode.light") : t("settings.displaySettings.colorMode.dark");

	const ColorModeIcon = colorMode === "auto" ? Monitor : isDark ? Sun : Moon;

	useEffect(() => {
		const fetchUserProfile = async () => {
			if (!user?.id) return;

			try {
				const { data, error } = await supabase
					.from('profiles')
					.select('id, first_name, last_name')
					.eq('id', user.id)
					.single();

				if (error) {
					console.error("Error fetching user profile:", error);
					return;
				}

				setProfileData(data);
			} catch (error) {
				console.error("Failed to fetch user profile:", error);
			}
		};

		fetchUserProfile();
	}, [user?.id]);

	if (!user) return null;

	const getInitials = () => {
		const email = user.email || "";
		return email.substring(0, 2).toUpperCase() || "U";
	};

	const getUserDisplayName = () => {
    
		if (profileData?.first_name && profileData?.last_name) {
			return `${profileData.first_name} ${profileData.last_name}`;
		}

		if (profileData?.first_name) {
			return profileData.first_name;
		}

    if (!user.email) return "User";
		return user.email.split('@')[0];
	};

	const handleOrganizationSwitch = async (orgId: string) => {
		setSwitchingOrgId(orgId);
		await switchOrganization(orgId);
		setSwitchingOrgId(null);
	};

	return (
		<div className="mt-auto p-2.5">
			{collapsed ? (
				<TooltipProvider>
					<DropdownMenu>
						<Tooltip>
							<TooltipTrigger asChild>
								<DropdownMenuTrigger asChild>
									<Button variant="ghost" size="icon" data-tour="user-menu" className="rounded-sm h-9 w-9 bg-primary/10 hover:bg-primary/20 transition-colors relative group">
										<span className="font-medium text-sm">{getInitials()}</span>
										<ChevronDown className="absolute -bottom-1 -right-1 h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-all duration-200 group-hover:scale-110" />
									</Button>
								</DropdownMenuTrigger>
							</TooltipTrigger>
							<TooltipContent>
								<p>{getUserDisplayName()}</p>
							</TooltipContent>
						</Tooltip>
						<DropdownMenuContent align="end" className="w-56">
									<div className="p-2">
										<p className="font-medium">{getUserDisplayName()}</p>
										<p className="text-sm text-muted-foreground">{user.email}</p>
									</div>
									<DropdownMenuSeparator />
									
									{organizations && organizations.length > 0 && (
										<>
											<div className="px-2 py-1.5 text-sm font-medium text-muted-foreground">
												Organizations
											</div>
											{organizations.map((org) => (
												<DropdownMenuItem 
													key={org.id}
													disabled={isLoading || org.id === currentOrganization?.id}
													className={cn(
														"cursor-pointer flex items-center justify-between",
														org.id === currentOrganization?.id && "bg-primary/10 font-medium"
													)}
													onClick={() => handleOrganizationSwitch(org.id)}
												>
													<div className="flex items-center">
														<Building className="mr-2 h-4 w-4" />
														<span>{org.name}</span>
													</div>
													{switchingOrgId === org.id && (
														<div className="h-3 w-3 animate-spin rounded-full border-2 border-primary border-t-transparent" />
													)}
												</DropdownMenuItem>
											))}
											<DropdownMenuSeparator />
										</>
									)}
									
									<DropdownMenuSeparator />
									
									<DropdownMenuItem asChild>
										<Link to="/settings" className="cursor-pointer">
											<Settings className="mr-2 h-4 w-4" />
											<span>{t('navigation.settings')}</span>
										</Link>
									</DropdownMenuItem>
									
									{(isSuperAdmin || isAdmin) && (
										<DropdownMenuItem asChild>
											<Link to="/admin" className="cursor-pointer">
												<Shield className="mr-2 h-4 w-4" />
												<span>{t('navigation.admin')}</span>
											</Link>
										</DropdownMenuItem>
									)}

									<DropdownMenuItem onClick={cycleColorMode} className="cursor-pointer">
										<ColorModeIcon className="mr-2 h-4 w-4" />
										<span>{colorModeLabel}</span>
									</DropdownMenuItem>

									<DropdownMenuItem asChild>
										<Link to="/help" className="cursor-pointer">
											<LifeBuoy className="mr-2 h-4 w-4" />
											<span>{t('navigation.help')}</span>
										</Link>
									</DropdownMenuItem>

									<DropdownMenuItem onClick={tour.start} className="cursor-pointer">
										<HelpCircle className="mr-2 h-4 w-4" />
										<span>{t('tour.restart')}</span>
									</DropdownMenuItem>

									<DropdownMenuSeparator />

									<DropdownMenuItem onClick={signOut} className="text-destructive cursor-pointer">
										<LogOut className="mr-2 h-4 w-4" />
										<span>Sign out</span>
									</DropdownMenuItem>
								</DropdownMenuContent>
					</DropdownMenu>
				</TooltipProvider>
			) : (
				<DropdownMenu>
					<DropdownMenuTrigger asChild>
						<div data-tour="user-menu" className="bg-muted/50 p-2 flex items-center cursor-pointer hover:bg-muted/80 transition-all duration-200 group relative rounded-sm">
							<div className="flex items-center justify-center h-9 w-9 rounded-sm bg-primary/10 text-foreground">
								<span className="font-medium text-sm">{getInitials()}</span>
							</div>
							<div className="flex-1 min-w-0 ml-3">
								<p className="font-medium truncate">{getUserDisplayName()}</p>
								<p className="text-xs text-muted-foreground truncate">{user.email}</p>
								{currentOrganization && (
									<div className="flex items-center mt-1">
										<Building className="h-3 w-3 mr-1 text-muted-foreground" />
										<span className="text-xs text-muted-foreground truncate">{currentOrganization.name}</span>
									</div>
								)}
							</div>
							<ChevronDown className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-all duration-200 ml-2 group-hover:translate-y-0.5" />
						</div>
					</DropdownMenuTrigger>
					<DropdownMenuContent align="end" className="w-56">
						<div className="p-2">
							<p className="font-medium">{getUserDisplayName()}</p>
							<p className="text-sm text-muted-foreground">{user.email}</p>
						</div>
						<DropdownMenuSeparator />
						
						{organizations && organizations.length > 0 && (
							<>
								<div className="px-2 py-1.5 text-sm font-medium text-muted-foreground">
									Organizations
								</div>
								{organizations.map((org) => (
									<DropdownMenuItem 
										key={org.id}
										disabled={isLoading || org.id === currentOrganization?.id}
										className={cn(
											"cursor-pointer flex items-center justify-between",
											org.id === currentOrganization?.id && "bg-primary/10 font-medium"
										)}
										onClick={() => handleOrganizationSwitch(org.id)}
									>
										<div className="flex items-center">
											<Building className="mr-2 h-4 w-4" />
											<span>{org.name}</span>
										</div>
										{switchingOrgId === org.id && (
											<div className="h-3 w-3 animate-spin rounded-full border-2 border-primary border-t-transparent" />
										)}
									</DropdownMenuItem>
								))}
								<DropdownMenuSeparator />
							</>
						)}
						
						<DropdownMenuSeparator />
						
						<DropdownMenuItem asChild>
							<Link to="/settings" className="cursor-pointer">
								<Settings className="mr-2 h-4 w-4" />
								<span>{t('navigation.settings')}</span>
							</Link>
						</DropdownMenuItem>
						
						{(isSuperAdmin || isAdmin) && (
							<DropdownMenuItem asChild>
								<Link to="/admin" className="cursor-pointer">
									<Shield className="mr-2 h-4 w-4" />
									<span>{t('navigation.admin')}</span>
								</Link>
							</DropdownMenuItem>
						)}

						<DropdownMenuItem onClick={cycleColorMode} className="cursor-pointer">
							<ColorModeIcon className="mr-2 h-4 w-4" />
							<span>{colorModeLabel}</span>
						</DropdownMenuItem>

						<DropdownMenuItem asChild>
							<Link to="/help" className="cursor-pointer">
								<LifeBuoy className="mr-2 h-4 w-4" />
								<span>{t('navigation.help')}</span>
							</Link>
						</DropdownMenuItem>

						<DropdownMenuItem onClick={tour.start} className="cursor-pointer">
							<HelpCircle className="mr-2 h-4 w-4" />
							<span>{t('tour.restart')}</span>
						</DropdownMenuItem>

						<DropdownMenuSeparator />

						<DropdownMenuItem onClick={signOut} className="text-destructive cursor-pointer">
							<LogOut className="mr-2 h-4 w-4" />
							<span>Sign out</span>
						</DropdownMenuItem>
					</DropdownMenuContent>
				</DropdownMenu>
			)}
		</div>
	);
};
